import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** 同一オリジンの相対パスだけをログイン後の遷移先にする。 */
export function isSafeRedirectPath(path: string | null | undefined): path is string {
  if (!path || /[\u0000-\u001f\u007f-\u009f]/.test(path)) return false;
  const trimmed = path.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//') || trimmed.includes('\\')) {
    return false;
  }
  if (/%(?:25)*(?:0[0-9a-f]|1[0-9a-f]|7f|2f|5c)/i.test(trimmed)) {
    return false;
  }
  return true;
}

/**
 * リダイレクト先を検証し、安全でなければデフォルト値にフォールバックする。
 * 戻り値は検証時に評価した trimmed 済みの値（検証結果と実際の使用値のズレを防ぐため）。
 */
export function getSafeRedirectPath(
  path: string | null | undefined,
  fallback: string = '/home'
): string {
  if (!isSafeRedirectPath(path)) return fallback;
  return path.trim();
}

/*
ログインを終えた人を一度送る口。サーバーが、LuaGate のチームの人は /luagate、それ以外は /home へ振り分ける。
ログイン画面の時点ではまだ誰か分からないので、画面の側では決められない
*/
export const SIGNED_IN_DISPATCH_PATH = '/signed-in';

/*
ログインや登録の画面そのもの。ログインのあとにここへ戻すと、ログイン済みの人が
またログイン画面に着いて輪になる。/signup/luagate/challenge のような下の階層は
ログインのあとに続きを出す画面なので含めない (完全一致で見る)
*/
const AUTH_PAGE_PATHS = new Set([
  '/login',
  '/signup',
  '/magic-link',
  '/passkey',
  '/verify-email',
  '/forgot-password',
  '/reset-password',
  '/teams/auth/login',
  '/teams/auth/signup',
  '/teams/auth/forgot-password',
  SIGNED_IN_DISPATCH_PATH,
]);

/**
 * ログインのあとの戻り先。getSafeRedirectPath の守り (同じオリジンの相対パスだけ) に加えて、
 * ログインや登録の画面そのものは既定の先に置き換える。
 */
export function getSafeLoginReturnPath(
  path: string | null | undefined,
  fallback: string = '/home'
): string {
  const safe = getSafeRedirectPath(path, fallback);
  // ブラウザと同じ形に直してから比べる。/x/../login や /%2e%2e/login はブラウザが /login に直す
  let pathname: string;
  try {
    pathname = decodeURIComponent(new URL(safe, 'https://base.invalid').pathname);
    pathname = new URL(pathname, 'https://base.invalid').pathname;
  } catch {
    return fallback;
  }
  pathname = pathname.replace(/\/+$/, '') || '/';
  return AUTH_PAGE_PATHS.has(pathname.toLowerCase()) ? fallback : safe;
}

/**
 * パスワードを忘れた人の画面 (忘れた、再設定、ログインへ戻る) をまたいで、ログインのあとの戻り先を持ち回る。
 * 支払いページ (/women/lp/pay?...) からログインに来た人が、再設定のあとも同じページに戻れるようにする。
 * 戻り先は getSafeLoginReturnPath と同じ守りを通したものだけを付ける (同じサイトの中の道だけ)。無ければ path のまま
 */
export function carryLoginReturnPath(path: string, returnPath: string | null | undefined): string {
  const safe = getSafeLoginReturnPath(returnPath, '');
  return safe ? `${path}?returnPath=${encodeURIComponent(safe)}` : path;
}

/**
 * ログイン画面への誘導URLを組み立てる（`/login?redirect=<path>` 規約）。
 * JWTセッション（30分TTL・リフレッシュなし）切れ検知時の各呼び出し元で
 * URLの組み立て方がバラつかないよう、ここに集約する。
 * 実際のredirect先の安全性検証（Open Redirect対策）はログイン画面側で
 * getSafeRedirectPath を通す前提。
 */
export function buildLoginRedirectUrl(currentPath: string): string {
  return `/login?redirect=${encodeURIComponent(currentPath)}`;
}

export function convertToRgba({
  color,
  opacity,
}: {
  color: string;
  opacity: number;
}): string {
  if (!color) {
    return `rgba(177, 177, 177, ${opacity})`;
  }

  color = color.trim();

  // If already rgba, extract rgb part and apply new opacity
  if (color.startsWith('rgba(')) {
    const match = color.match(/rgba?\(([^)]+)\)/);
    if (match) {
      const values = match[1].split(',').map((v) => v.trim());
      if (values.length >= 3) {
        return `rgba(${values[0]}, ${values[1]}, ${values[2]}, ${opacity})`;
      }
    }
  }

  // If rgb, just add opacity
  if (color.startsWith('rgb(')) {
    const match = color.match(/rgb\(([^)]+)\)/);
    if (match) {
      return `rgba(${match[1]}, ${opacity})`;
    }
  }

  // If hex color
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    let r: number, g: number, b: number;

    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length === 6) {
      r = parseInt(hex.slice(0, 2), 16);
      g = parseInt(hex.slice(2, 4), 16);
      b = parseInt(hex.slice(4, 6), 16);
    } else {
      return `rgba(177, 177, 177, ${opacity})`;
    }

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  // If plain rgb values like "255, 255, 255"
  if (/^\d+,\s*\d+,\s*\d+$/.test(color)) {
    return `rgba(${color}, ${opacity})`;
  }

  // Fallback
  return `rgba(177, 177, 177, ${opacity})`;
}
